'use strict';
import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';

import { FaBan, FaExclamationCircle } from "react-icons/fa";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {allTrue, pluralize} from "@uw-it-sis/lib-react/lib/Utils";

import addEditCourseOrPlaceholderFormStore from './AddEditCourseOrPlaceholderFormStore';
import {isCoursePlaceholder} from './CourseUtils';
import bookmarksStore from './BookmarksStore';
import {GeneralPlaceholders, getPlaceholderDescriptionFromCode} from './GeneralPlaceHolderConstants';
import GenEdUtils from './GenEdUtils';
import {PLAN_ITEM_NOTE_LIMIT} from "./Constants";

/**
 * Component to create a control for credit input
 */
const AddEditCreditControl = (props) => {
    const handleMaxLength = e => {
        const target = e.target;
        const value = target.value;
        if ( value.length <= target.maxLength ) {
            return true;
        } else {
            target.value = value.substring(0, value.length - 1);
        }
    };

    return (
        <Form.Group as={Col} xs={5} controlId="add-credit" className="mb-0">
            <Form.Label>Credits (Optional)</Form.Label>
            <Form.Control as="input"
                          type="number"
                          placeholder="Credits"
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          maxLength="2"
                          min={1}
                          max={25}
                          pattern="[0-9][0-9]"
                          name="credit"
                          value={props.value}
                          onBlur={props.onBlur}
                          onChange={props.onChange}
                          onInput={handleMaxLength}
                          aria-label="Credits (optional)"
                          aria-invalid={! props.state.validCredit}
                          isInvalid={! props.state.validCredit} />
            <Form.Control.Feedback type="invalid">
                <Alert variant="danger" className="alert-sm d-inline-flex align-items-center mb-0">
                    <FaExclamationCircle aria-hidden="true" className="me-2 mb-0"/>
                    Must be between 1-25
                </Alert>
            </Form.Control.Feedback>
        </Form.Group>
    );
}

AddEditCreditControl.propTypes = {
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    state: PropTypes.object.isRequired,
    value: PropTypes.string
}

/**
 * Allows users to add or edit a placeholder
 */

class AddEditCourseOrPlaceholderForm extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = addEditCourseOrPlaceholderFormStore;

        this.state = this.initialState;

        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.validateCredit = this.validateCredit.bind(this);
        this.validateField = this.validateField.bind(this);
    }

    // Create a validation state for form entry
    get initialState() {
        return {
            validCredit: true,
            validField: {
                courseCode: true,
                placeHolderText: true,
                genedCode: true
            }
        }
    }

    componentDidMount() {
        this.store.mount(this.props.termId, this.props.formData);
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if ( this.store.state === States.done ) {
            // Auto close the modal when a plan item successfully added
            if ( this.store.isSuccessful() ) {
                this.props.onCancel();
            }

            // Set the focus to radio input on initial form load
            if ( this.store.isInitialForm() ) {
                this[this.store.getAddPlanSelector()].focus();
            }
        }
    }

    /**
     * Callback to the parent component when the cancel link is clicked.
     */
    handleCancelClick() {
        this.props.onCancel();
    }

    /**
     * Handle input widget changes.
     */
    onChange(e) {
        let property = e.target.name;
        let value = e.target.value;
        if ( property === "isBackup" ) {
            value = e.target.checked;
        } else if ( value === null || value === undefined ) {
            value = "";
        }

        // Reset the validation state when the addToPlanSelector changes
        if ( property === "addToPlanSelector" && value !== "" ) {
            this.setState(this.initialState);
        }

        this.store.updateFormData(property, value);
    }

    /**
     * Handle the credit validation and update state
     */
    validateCredit(e) {
        const target = e.currentTarget;
        const value = target.value;
        // Check value is between min and max
        let validDigits = (parseInt(value) >= target.min && parseInt(value) <= target.max);
        // Check is value is empty
        let valueEmpty = (value === "");
        // Update the state whether credit input is valid
        this.setState({validCredit: (validDigits || valueEmpty)});
    }

    validateField(e) {
        const target = e.currentTarget;
        const value = target.value;
        // Check is value is empty
        let valueEmpty = (value === "");
        let validField = this.state.validField;
        validField[target.name] = ! valueEmpty;
        this.setState({validField: validField});
    }

    /**
     * Handle form submit.
     */
    onSubmit(e) {
        e.preventDefault();  //  Prevent POST and reload.
        this.store.submitData();
    }
    _makeCourseComponent(){
        let courseComponent;
        if ( this.store.getAddPlanSelector() === "course" ) {
            // Credits input is only shown for placeholders
            let placeholderCredits;
            if ( this.store.getAddPlanSelector() === "course" && this.store.getFormData()["courseCode"] && isCoursePlaceholder(this.store.getFormData()["courseCode"]) ) {
                placeholderCredits =
                    <AddEditCreditControl onBlur={this.validateCredit}
                                          onChange={this.onChange}
                                          state={this.state}
                                          value={this.store.getFormData()["credit"]} />;
            }

            courseComponent =
                <Row>
                    <Form.Group as={Col} xs={7} controlId="add-course-type" className="mb-0">
                        <Form.Label>Course Code or Course Placeholder</Form.Label>
                        <Form.Control
                            type="text"
                            name="courseCode"
                            value={this.store.getAddPlanSelector() === "course" ? this.store.getFormData()["courseCode"] : ""}
                            aria-label="Course Code or Course Placeholder"
                            required={this.store.getAddPlanSelector() === "course"}
                            aria-hidden={this.store.getAddPlanSelector() !== "course"}
                            maxLength={10}
                            onBlur={this.validateField}
                            aria-invalid={! this.state.validField.courseCode}
                            isInvalid={! this.state.validField.courseCode}
                            placeholder="e.g. ENGL 102, MATH 1xx, etc."
                            onChange={this.onChange} />
                        <Form.Control.Feedback type="invalid">
                            You must enter a course code or placeholder
                        </Form.Control.Feedback>
                    </Form.Group>
                    {placeholderCredits}
                </Row>;

        }
        return courseComponent;
    }

    _makeBookmarkComponent(bookmarkList) {
        let bookmarkComponent;
        // Do not give the bookmark as option when there are no bookmarks to choose from
        if ( ! appStore.isAdviser() && bookmarkList.length > 0 && this.store.getAddPlanSelector() === "bookmark") {
            let bookmarkOptions = bookmarkList.map(function (code, i) {
                return <option value={code} key={i}>{code}</option>;
            });

            bookmarkComponent =
                <Form.Group className="mb-0">
                    <Form.Label>Bookmarked courses</Form.Label>
                    <Form.Select
                        name="courseCode"
                        onChange={this.onChange}
                        aria-label="Bookmarked courses"
                        required={this.store.getAddPlanSelector() === "bookmark"}
                        aria-hidden={this.store.getAddPlanSelector() !== "bookmark"}
                        disabled={this.store.state === States.pending}
                        onBlur={this.validateField}
                        aria-invalid={! this.state.validField.courseCode}
                        isInvalid={! this.state.validField.courseCode}>
                        <option value="">Select a Bookmark</option>
                        {bookmarkOptions}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        You must select a bookmark
                    </Form.Control.Feedback>
                </Form.Group>;
        }
        return bookmarkComponent;
    }

    _makePlaceholderComponent() {
        let placeholderComponent;

        if ( this.store.getAddPlanSelector() === "placeholder") {
            let customPlaceHolder;
            let genedComponent;

            if ( this.store.getFormData()["courseCode"] === GeneralPlaceholders.OTHER ){
                customPlaceHolder =
                    <Form.Group as={Col} xs={12} sm={7} className="mb-0 mt-4">
                        <Form.Label>Enter a custom placeholder</Form.Label>
                        <Form.Control
                            placeholder="e.g. Apply for Major"
                            type="text"
                            name="placeHolderText"
                            value={this.store.getFormData()["placeHolderText"]}
                            maxLength={40}
                            onChange={this.onChange}
                            required={this.store.getFormData()["courseCode"] === GeneralPlaceholders.OTHER}
                            onBlur={this.validateField}
                            aria-invalid={! this.state.validField.placeHolderText}
                            isInvalid={! this.state.validField.placeHolderText} />
                        <Form.Text className="d-flex justify-content-end">
                            <strong>{40 - this.store.getFormData()["placeHolderText"].length}</strong>&nbsp;
                            <span>{pluralize("character", (40 - this.store.getFormData()["placeHolderText"].length))} remaining</span>
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                            You must enter a placeholder
                        </Form.Control.Feedback>
                    </Form.Group>;
            } // If the user selects General Education or one of the Gened types
            else if (this.store.getFormData()["courseCode"] === GeneralPlaceholders.GENERALEDUCATION || GenEdUtils.doesGenedCodeExist(this.store.getFormData()["courseCode"])){
                let genedList = GenEdUtils.getSortedTitleList();
                let genedReqOptions = genedList.map(function (gened, i) {
                    return <option value={gened.genedCode} key={i}>{gened.title}&nbsp;({gened.code})</option>;
                });
                genedComponent =
                    <Form.Group as={Col} xs={12} sm={7} className="mb-0 mt-4">
                        <Form.Label>Select a Gen Ed Req option</Form.Label>
                        <Form.Select
                            name="genedCode"
                            value={this.store.getFormData()["genedCode"] ? this.store.getFormData()["genedCode"] : this.store.getFormData()["courseCode"] }
                            onChange={this.onChange}
                            aria-label="Select a General Education (Gen Ed) option"
                            disabled={this.store.state === States.pending}
                            required={this.store.getFormData()["courseCode"] === GeneralPlaceholders.GENERALEDUCATION || GenEdUtils.doesGenedCodeExist(this.store.getFormData()["courseCode"])}
                            onBlur={this.validateField}
                            aria-invalid={! this.state.validField.genedCode}
                            isInvalid={! this.state.validField.genedCode}>
                            <option value="">Select a General Education (Gen Ed) option</option>
                            {genedReqOptions}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            You must select a gen ed requirement
                        </Form.Control.Feedback>
                    </Form.Group>;
            }
            let value ="";
            if (this.store.getAddPlanSelector() === "placeholder") {
                // If the course code was one of the gened types then the user selected General education as the placeholder
                if (GenEdUtils.doesGenedCodeExist(this.store.getFormData()["courseCode"])) {
                    value = GeneralPlaceholders.GENERALEDUCATION;
                } else {
                    value = this.store.getFormData()["courseCode"];
                }
            }
            placeholderComponent =
                <Row>
                    <Form.Group as={Col} xs={7} controlId="add-placeholder-type" className="mb-0">
                        <Form.Label>General Placeholder Type</Form.Label>
                        {/*Fix me: For now the values are hard-coded but we need a Rest api to get these values from the database*/}
                        <Form.Select
                            name="courseCode"
                            value={value}
                            onChange={this.onChange}
                            aria-label="General Placeholder Type"
                            required={this.store.getAddPlanSelector() === "placeholder"}
                            aria-hidden={this.store.getAddPlanSelector() !== "placeholder"}
                            onBlur={this.validateField}
                            aria-invalid={! this.state.validField.courseCode}
                            isInvalid={! this.state.validField.courseCode}>
                            <option value="">Select a placeholder</option>
                            <option value={GeneralPlaceholders.ELECTIVE}>{getPlaceholderDescriptionFromCode(GeneralPlaceholders.ELECTIVE)}</option>
                            <option value={GeneralPlaceholders.STUDYABROAD}>{getPlaceholderDescriptionFromCode(GeneralPlaceholders.STUDYABROAD)}</option>
                            <option value={GeneralPlaceholders.GENERALEDUCATION}>{getPlaceholderDescriptionFromCode(GeneralPlaceholders.GENERALEDUCATION)} ({GenEdUtils.exampleList.join(", ")}, etc.)</option>
                            <option value={GeneralPlaceholders.FL}>{getPlaceholderDescriptionFromCode(GeneralPlaceholders.FL)}</option>
                            <option value={GeneralPlaceholders.OTHER}>{getPlaceholderDescriptionFromCode(GeneralPlaceholders.OTHER)}</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            You must select a placeholder
                        </Form.Control.Feedback>
                    </Form.Group>
                    <AddEditCreditControl onBlur={this.validateCredit}
                                          onChange={this.onChange}
                                          state={this.state}
                                          value={this.store.getFormData()["credit"]} />
                    {customPlaceHolder}
                    {genedComponent}
                </Row>;
        }
        return placeholderComponent;
    }

    _makeBackupComponent() {
        let backupComponent;
        // If its not in adviser view and a planItem is being added
        if ( ! appStore.isAdviser() && this.store.getFormData()["planItemId"] === "" ) {
            backupComponent =
                <Form.Group className="my-4">
                    <Form.Check id="mark-backup"
                                type="checkbox">
                        <Form.Check.Input type="checkbox"
                                          name="isBackup"
                                          checked={this.store.getFormData()["isBackup"]}
                                          aria-checked={this.store.getFormData()["isBackup"]}
                                          onChange={this.onChange}
                                          className="align-middle" />
                        <Form.Check.Label className="align-middle">
                            Mark as 'Backup'
                        </Form.Check.Label>
                    </Form.Check>
                </Form.Group>;
        }
        return backupComponent
    }

    _makeButtonComponent () {
        let buttonComponent;
        if ( ! appStore.isAdviser() ) {
            buttonComponent =
                <Button id="add-to-plan"
                        type="submit"
                        variant="primary"
                        disabled={this.store.disableAddToPlan || this.store.state === States.pending || !allTrue(this.state)}>
                    {this.store.getFormData()["planItemId"] === "" ? "Add to plan" : "Update"}
                </Button>;
        } else {
            buttonComponent =
                <Button id="recommend"
                        type="submit"
                        variant="primary"
                        disabled={this.store.disableAddToPlan || this.store.state === States.pending || !allTrue(this.state)}>
                    Recommend
                </Button>;
        }
        return buttonComponent;
    }

    render() {
        // Build messaging for if there was a failure to add
        let failureMessage;
        if ( this.store.state === States.error && this.store.getAddFailedMessage() && "" !== this.store.getAddFailedMessage() ) {
            failureMessage =
                <Alert variant="danger" className="mb-0 mt-3 d-flex align-items-start">
                    <FaBan className="me-2 mt-1" aria-hidden={true} />
                    <span>{this.store.getAddFailedMessage()}</span>
                </Alert>
        }

        let title;
        if ( ! appStore.isAdviser() ) {
            if ( this.store.getFormData()["planItemId"] === "" ) {
                title = "What would you like to add?"
            } else {
                title = "Update the planned item";
            }
        } else {
            title = "What would you like to recommend?"
        }

        let bookmarkList = [];
        let bookmarkRadioOption;
        if ( ! appStore.isAdviser() ) {
            bookmarkList = bookmarksStore.getCourseBookmarkCodes();
            // Do not give the bookmark as option when there are no bookmarks to choose from
            if (bookmarkList.length > 0 ) {
                bookmarkRadioOption =
                    <Form.Check id="bookmarked-course"
                                type="radio"
                                className="mt-1">
                        <Form.Check.Input type="radio"
                                          name="addToPlanSelector"
                                          checked={this.store.getAddPlanSelector() === "bookmark"}
                                          aria-checked={this.store.getAddPlanSelector() === "bookmark"}
                                          onChange={this.onChange}
                                          value="bookmark"
                                          className="align-middle"
                                          ref={(input) => { this.bookmark = input; }} />
                        <Form.Check.Label className="align-middle">
                            Bookmarked course
                        </Form.Check.Label>
                    </Form.Check>;
            }
        }

        const noteMaxLength = PLAN_ITEM_NOTE_LIMIT;
        let noteRemainLength = Math.max(0, (noteMaxLength - this.store.getFormData()["note"].length));

        return (
            <Form onSubmit={this.onSubmit}>
                <Form.Group controlId="update-placeholder">
                    <Form.Label className="fw-bolder lead mb-3">{title}</Form.Label>
                    <Form.Group>
                        <Form.Check id="course-placeholder"
                                    type="radio">
                            <Form.Check.Input type="radio"
                                              name="addToPlanSelector"
                                              onChange={this.onChange}
                                              checked={this.store.getAddPlanSelector() === "course"}
                                              aria-checked={this.store.getAddPlanSelector() === "course"}
                                              value="course"
                                              className="align-middle"
                                              ref={(input) => { this.course = input; }} />
                            <Form.Check.Label className="align-middle">
                                Course or course placeholder
                            </Form.Check.Label>
                            <span className="ms-2 align-middle">(e.g. MATH 126, ENGL 3xx)</span>
                        </Form.Check>
                        {bookmarkRadioOption}
                        <Form.Check id="general-placeholder"
                                    type="radio"
                                    className="mt-1">
                            <Form.Check.Input type="radio"
                                              name="addToPlanSelector"
                                              checked={this.store.getAddPlanSelector() === "placeholder"}
                                              aria-checked={this.store.getAddPlanSelector() === "placeholder"}
                                              onChange={this.onChange}
                                              value="placeholder"
                                              className="align-middle"
                                              ref={(input) => { this.placeholder = input; }} />
                            <Form.Check.Label className="align-middle">
                                General placeholder
                            </Form.Check.Label>
                            <span className="ms-2 align-middle">(e.g. Elective, Gen Ed Req, Other, etc)</span>
                        </Form.Check>
                    </Form.Group>
                    <div className="my-4" aria-live="polite" aria-atomic="true">
                        {this._makeCourseComponent()}
                        {this._makeBookmarkComponent(bookmarkList)}
                        {this._makePlaceholderComponent()}
                    </div>
                    {this._makeBackupComponent()}
                    <Form.Group controlId="add-note">
                        <Form.Label>
                            {appStore.isAdviser() ? "Describe recommendation reason" : "Notes (Optional)"}
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            size="sm"
                            name="note"
                            value={this.store.getFormData()["note"]}
                            maxLength={noteMaxLength}
                            placeholder={`Enter a short note (${noteMaxLength} character max)`}
                            onChange={this.onChange} />
                        <Form.Text className="d-flex justify-content-end" aria-live={noteRemainLength > 10 ? 'off':'polite'} aria-atomic={true}>
                            <strong>{noteRemainLength}</strong>&nbsp;
                            <span>{pluralize("character", noteRemainLength)} remaining</span>
                        </Form.Text>
                    </Form.Group>
                </Form.Group>
                <ButtonToolbar className="justify-content-end mt-3">
                    {this._makeButtonComponent()}
                    <Button id="add-to-plan-cancel" variant="secondary" onClick={this.handleCancelClick} type="reset">Cancel</Button>
                </ButtonToolbar>
                <div role="alert" aria-live="assertive" aria-atomic="true">
                    {failureMessage}
                </div>
            </Form>
        );
    }
}

AddEditCourseOrPlaceholderForm.propTypes = {
    context: PropTypes.string.isRequired,
    termId: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    formData: PropTypes.object
};

export default observer(AddEditCourseOrPlaceholderForm);